// “Which customer?” as a form field.
//
// Every other picker in the kit chooses from a list the screen already holds —
// resources, services, weekdays. Customers cannot work that way: there are 4.5k
// of them, so one page of a list is never the person being looked for, and a
// select box would be a scroll through strangers.
//
// It searches with `useCustomerSearch`, the SAME debounce and stale-response
// guard the composer's customer pin and @-mentions use, which in turn runs the
// same query as the Customers screen. A name that finds someone there finds them
// here — an operator should not have to learn two searches.
//
// The selection is a whole named person, never a typed id: an operator picking a
// customer for a package sale is choosing between named things, which is the line
// that keeps these money screens on the right side of the quick-action rule.

import { useState } from 'react'
import { X } from 'lucide-react'
import { Spinner } from '@/components/Spinner'
import { TextField } from '@/components/editor/EditorKit'
import { MIN_QUERY_LENGTH, useCustomerSearch, type CustomerMatch } from '@/hooks/useCustomerSearch'

export interface PickedCustomer {
  id: string
  name: string
  phone: string | null
}

export function CustomerField({
  value,
  onChange,
  disabled,
  placeholder,
}: {
  value: PickedCustomer | null
  onChange: (customer: PickedCustomer | null) => void
  disabled?: boolean
  placeholder?: string
}) {
  const [query, setQuery] = useState('')
  // Nothing is searched once somebody is chosen — the field is then a statement,
  // not a search box.
  const { matches, searching, active } = useCustomerSearch(query, { enabled: !value && !disabled, limit: 6 })

  if (value) {
    return (
      <div className="flex items-center gap-2 rounded-md border border-input px-2.5 py-2">
        <span className="min-w-0 flex-1">
          <span className="block truncate text-[13px] font-semibold text-foreground">{value.name}</span>
          {value.phone ? (
            <span className="block truncate text-[11px] text-muted-foreground">{value.phone}</span>
          ) : null}
        </span>
        <button
          type="button"
          disabled={disabled}
          onClick={() => { onChange(null); setQuery('') }}
          aria-label={`Choose someone other than ${value.name}`}
          title="Choose someone else"
          className="shrink-0 rounded-md p-1 text-muted-foreground hover:bg-muted hover:text-foreground disabled:opacity-40"
        >
          <X size={15} aria-hidden />
        </button>
      </div>
    )
  }

  const choose = (m: CustomerMatch) => {
    onChange({ id: m.id, name: m.name ?? 'Unnamed customer', phone: m.phone })
    setQuery('')
  }

  return (
    <div>
      <TextField
        value={query}
        onChange={setQuery}
        placeholder={placeholder ?? 'Search by name, phone or email…'}
        disabled={disabled}
      />

      {query.trim().length > 0 ? (
        <div className="mt-1 max-h-52 overflow-y-auto rounded-md border border-border bg-card">
          {searching ? (
            <div className="flex items-center gap-2 px-2.5 py-2 text-muted-foreground">
              <Spinner size={14} className="text-current" />
              <span className="text-[12.5px]">Searching…</span>
            </div>
          ) : !active ? (
            <p className="px-2.5 py-2 text-[12.5px] text-muted-foreground">
              Keep typing — {MIN_QUERY_LENGTH} characters or more.
            </p>
          ) : matches.length === 0 ? (
            <p className="px-2.5 py-2 text-[12.5px] text-muted-foreground">Nobody matches that.</p>
          ) : (
            matches.map((m) => (
              <button
                key={m.id}
                type="button"
                onClick={() => choose(m)}
                className="flex w-full flex-col border-b border-muted px-2.5 py-2 text-left last:border-b-0 hover:bg-muted"
              >
                <span className="truncate text-[13px] text-foreground">{m.name ?? 'Unnamed customer'}</span>
                {m.phone ? <span className="truncate text-[11px] text-muted-foreground">{m.phone}</span> : null}
              </button>
            ))
          )}
        </div>
      ) : null}
    </div>
  )
}
